import { useCallback, useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, MoreHorizontal, Paperclip, GripVertical, Plus } from 'lucide-react';
import type { Task, CustomFieldValue } from '@/types';
import { isOverdue } from '@/utils/formatters';
import { parseDuration, formatDuration } from '@/utils/duration';
import { isCompletedStatus } from '@/constants/taskStatuses';
import type { TaskStatusDef } from '@/db/taskStatuses';
import { createTag } from '@/db/tags';
import {
  StatusSelect,
  PrioritySelect,
  InlineDatePicker,
  AssigneesEditor,
  TagsEditor,
  EditableText,
} from '@/components/shared/inline-editors';
import { CustomFieldEditor } from '@/components/fields/CustomFieldEditor';
import type { CustomField } from '@/db/customFields';
import clsx from 'clsx';
import type { ColumnId } from '@/hooks/useListColumns';

// ─── InlineMinutesInput ────────────────────────────────────────────────────────

function InlineMinutesInput({ value, onChange, disabled }: { value: number | null | undefined; onChange: (ms: number | null) => void; disabled?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const display = value ? formatDuration(value) : null;

  if (!editing) {
    return (
      <button
        onClick={() => { if (!disabled) { setDraft(value ? formatDuration(value) : ''); setEditing(true); } }}
        className={clsx('text-[11px] w-full text-left', display ? 'text-text-tertiary' : 'text-text-quaternary/50 hover:text-text-quaternary')}
        disabled={disabled}
      >
        {display || '—'}
      </button>
    );
  }

  return (
    <input
      type="text"
      value={draft}
      autoFocus
      onChange={e => setDraft(e.target.value)}
      onBlur={() => {
        const parsed = parseDuration(draft);
        onChange(parsed !== null && parsed > 0 ? parsed : null);
        setEditing(false);
      }}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.currentTarget.blur(); }
        if (e.key === 'Escape') { setEditing(false); }
      }}
      placeholder="es. 1h 30m"
      className="w-full px-1 py-0.5 text-[11px] bg-surface-tertiary border border-accent-violet rounded text-text-primary focus:outline-none"
    />
  );
}

interface TaskRowProps {
  task: Task;
  subtasks?: Task[];
  level?: number;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onTaskClick?: (task: Task) => void;
  onUpdate?: (taskId: string, updates: Partial<Task>) => void;
  availableAssignees?: string[];
  orgMemberNames?: string[];
  availableTags?: string[];
  isSelected?: boolean;
  isChecked?: boolean;
  onCheckChange?: (taskId: string, checked: boolean) => void;
  onContextMenu?: (e: React.MouseEvent, task: Task) => void;
  onAddSubtask?: () => void;
  isDragging?: boolean;
  isDragOver?: boolean;
  dropPosition?: 'before' | 'into' | 'after' | null;
  onDragStart?: (e: React.DragEvent, taskId: string) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent, taskId: string) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, targetTaskId: string) => void;
  visibleColumns?: Set<ColumnId>;
  customFieldCols?: CustomField[];
  customStatuses?: TaskStatusDef[];
  getColumnWidth?: (id: string) => number;
  nameColWidth?: number;
}

export function TaskRow({
  task,
  subtasks = [],
  level = 0,
  isExpanded = false,
  onToggleExpand,
  onTaskClick,
  onUpdate,
  availableAssignees = [],
  orgMemberNames,
  availableTags = [],
  isSelected = false,
  isChecked = false,
  onCheckChange,
  onContextMenu,
  onAddSubtask,
  isDragging = false,
  isDragOver = false,
  dropPosition = null,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  visibleColumns,
  customFieldCols = [],
  customStatuses,
  getColumnWidth,
  nameColWidth = 350,
}: TaskRowProps) {
  const colW = (id: string, fallback: number) => getColumnWidth ? getColumnWidth(id) : fallback;
  const hasSubtasks = subtasks.length > 0;
  const overdue = isOverdue(task.dueDate);
  const isCompleted = isCompletedStatus(task.status);
  const completedSubtasks = subtasks.filter((s) => isCompletedStatus(s.status)).length;
  const subtaskProgress = hasSubtasks ? completedSubtasks / subtasks.length : 0;

  const canEdit = !!onUpdate;

  // Responsive tag display: 4 tags on md/lg, 5 tags on xl+
  const [_maxTagsDisplay, setMaxTagsDisplay] = useState(4);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const xlQuery = window.matchMedia('(min-width: 1280px)');
    const updateMaxTags = () => {
      setMaxTagsDisplay(xlQuery.matches ? 5 : 4);
    };

    updateMaxTags();
    xlQuery.addEventListener('change', updateMaxTags);
    return () => xlQuery.removeEventListener('change', updateMaxTags);
  }, []);

  const handleUpdate = useCallback(
    (updates: Partial<Task>) => {
      if (onUpdate) {
        onUpdate(task.id, updates);
      }
    },
    [onUpdate, task.id]
  );

  const handleAddAssignee = useCallback(
    (assignee: string) => {
      const assignees = task.assignees || [];
      if (!assignees.includes(assignee)) {
        handleUpdate({ assignees: [...assignees, assignee] });
      }
    },
    [task.assignees, handleUpdate]
  );

  const handleRemoveAssignee = useCallback(
    (assignee: string) => {
      const assignees = task.assignees || [];
      handleUpdate({ assignees: assignees.filter((a) => a !== assignee) });
    },
    [task.assignees, handleUpdate]
  );

  const _handleAddTag = useCallback(
    (tag: string) => {
      const tags = task.tags || [];
      if (!tags.includes(tag)) {
        handleUpdate({ tags: [...tags, tag] });
      }
    },
    [task.tags, handleUpdate]
  );

  const _handleRemoveTag = useCallback(
    (tag: string) => {
      const tags = task.tags || [];
      handleUpdate({ tags: tags.filter((t) => t !== tag) });
    },
    [task.tags, handleUpdate]
  );

  const handleCreateNewTag = useCallback(
    (tagName: string) => {
      if (!task.spaceId) return;
      createTag({
        name: tagName,
        color: null,
        scope: 'list',
        scope_id: task.spaceId,
        order_index: 0,
        created_at: Date.now(),
      }).catch(() => {}); // fire-and-forget, tag is already on the task
    },
    [task.spaceId]
  );

  const handleRowClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't open if clicking on interactive elements
      const target = e.target as HTMLElement;
      if (
        target.closest('button') ||
        target.closest('input') ||
        target.closest('select') ||
        target.closest('[role="button"]') ||
        target.closest('.stop-propagation')
      ) {
        return;
      }
      // Single click to open task
      onTaskClick?.(task);
    },
    [task, onTaskClick]
  );

  return (
    <>
      <div
        onClick={handleRowClick}
        onContextMenu={onContextMenu ? (e) => { e.preventDefault(); onContextMenu(e, task); } : undefined}
        onDragOver={onDragOver ? (e) => { e.preventDefault(); onDragOver(e, task.id); } : undefined}
        onDragLeave={onDragLeave}
        onDrop={onDrop ? (e) => { e.preventDefault(); onDrop(e, task.id); } : undefined}
        className={clsx(
          'group flex items-center gap-1.5 border-b border-border-primary/30 transition-colors h-10 relative cursor-pointer',
          level > 0 && 'bg-surface-tertiary/20',
          isSelected && 'bg-accent-violet/10',
          isDragging && 'opacity-40',
          isDragOver && dropPosition === 'into' && 'bg-accent-violet/10',
        )}
      >
        {/* Drop position indicators */}
        {isDragOver && dropPosition === 'before' && (
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-accent-violet z-20 pointer-events-none" />
        )}
        {isDragOver && dropPosition === 'after' && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent-violet z-20 pointer-events-none" />
        )}

        {/* ── Sticky name section ── */}
        <div
          className={clsx(
            'sticky left-0 z-10 flex items-center gap-1.5 px-2.5 py-2 shrink-0 h-full',
            // Background must be opaque to cover scrolling columns behind
            isSelected ? 'bg-accent-violet/10' : level > 0 ? 'bg-[var(--color-surface-tertiary,#f8f8f8)]/95' : 'bg-[var(--color-surface-primary,#fff)]',
            'group-hover:bg-surface-hover/90',
          )}
          style={{ width: nameColWidth, paddingLeft: `${12 + level * 20}px` }}
        >
          {isSelected && <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-accent-violet" />}

          {/* Subtask progress bar */}
          {hasSubtasks && subtaskProgress > 0 && (
            <div className="absolute bottom-0 left-0 right-0 h-px bg-border-primary/20 pointer-events-none">
              <div
                className={clsx(
                  'h-full transition-all duration-300',
                  subtaskProgress === 1 ? 'bg-accent-emerald' : 'bg-accent-violet/60'
                )}
                style={{ width: `${subtaskProgress * 100}%` }}
              />
            </div>
          )}

          {/* Drag handle */}
          {onDragStart && (
            <div
              draggable
              onDragStart={(e) => { e.stopPropagation(); onDragStart(e, task.id); }}
              onDragEnd={onDragEnd}
              className="absolute inset-y-0 flex items-center justify-center opacity-0 group-hover:opacity-50 hover:!opacity-100 cursor-grab active:cursor-grabbing z-10 px-0.5"
              style={{ left: `${Math.max(2, (level || 0) * 20 - 2)}px`, width: '12px' }}
              title="Trascina per spostare"
            >
              <GripVertical className="w-2.5 h-2.5 text-text-quaternary" />
            </div>
          )}

          {/* Selection Checkbox */}
          {onCheckChange && (
            <div className="w-5 shrink-0 flex items-center justify-center">
              <input
                type="checkbox"
                className="w-3.5 h-3.5 rounded border-border-primary cursor-pointer"
                checked={isChecked}
                onChange={(e) => {
                  e.stopPropagation();
                  onCheckChange(task.id, e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {/* Expand Toggle */}
          <div className="w-3.5 shrink-0">
            {hasSubtasks && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand?.();
                }}
                className="p-0.5 -ml-0.5 rounded hover:bg-surface-active transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="w-3 h-3 text-text-quaternary" />
                ) : (
                  <ChevronRight className="w-3 h-3 text-text-quaternary" />
                )}
              </button>
            )}
          </div>

          {/* Status - Inline Editable */}
          <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
            <StatusSelect
              value={task.status}
              onChange={(status) => handleUpdate({ status })}
              size="sm"
              showLabel={false}
              disabled={!canEdit}
              customStatuses={customStatuses}
            />
          </div>

          {/* Priority - Inline Editable */}
          {(!visibleColumns || visibleColumns.has('priority')) && (
            <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
              <PrioritySelect
                value={task.priority ?? null}
                onChange={(priority) => handleUpdate({ priority })}
                size="sm"
                showLabel={false}
                disabled={!canEdit}
              />
            </div>
          )}

          {/* Task Name */}
          <div className="flex-1 min-w-0 relative flex items-center">
            <div className="min-w-0 flex-1 stop-propagation">
              {canEdit ? (
                <EditableText
                  value={task.name}
                  onChange={(name) => handleUpdate({ name })}
                  className={clsx(
                    'text-[12px] truncate leading-tight block',
                    isCompleted
                      ? 'text-text-tertiary line-through decoration-text-quaternary'
                      : 'text-text-primary'
                  )}
                  inputClassName="text-[12px]"
                  disabled={!canEdit}
                />
              ) : (
                <span
                  className={clsx(
                    'text-[12px] truncate leading-tight block hover:text-accent-violet transition-colors',
                    isCompleted
                      ? 'text-text-tertiary line-through decoration-text-quaternary'
                      : 'text-text-primary'
                  )}
                >
                  {task.name}
                </span>
              )}
            </div>
            {hasSubtasks && (
              <span className={clsx(
                'shrink-0 ml-1.5 px-1 py-0.5 rounded text-[10px] font-medium tabular-nums',
                subtaskProgress === 1
                  ? 'bg-accent-emerald/15 text-accent-emerald'
                  : 'bg-surface-tertiary text-text-quaternary'
              )}>
                {completedSubtasks}/{subtasks.length}
              </span>
            )}
            {(task.attachments || []).length > 0 && (
              <span className="shrink-0 ml-1.5 flex items-center gap-0.5 text-text-quaternary">
                <Paperclip className="w-3 h-3" />
                <span className="text-[10px] tabular-nums">{(task.attachments || []).length}</span>
              </span>
            )}
            {/* Hover actions — absolutely positioned to not consume layout space */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-0.5 bg-surface-hover rounded pl-1">
              {canEdit && onAddSubtask && (
                <button
                  onClick={(e) => { e.stopPropagation(); onAddSubtask(); }}
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-accent-violet/10 text-text-quaternary hover:text-accent-violet transition-all text-[10px]"
                  title="Aggiungi subtask"
                >
                  <Plus className="w-3 h-3" />
                  <span>Aggiungi subtask</span>
                </button>
              )}
              {canEdit && (
                <button
                  onClick={(e) => { e.stopPropagation(); onTaskClick?.(task); }}
                  className="p-0.5 rounded hover:bg-surface-active text-text-quaternary hover:text-accent-violet transition-colors"
                  title="Apri dettaglio"
                >
                  <MoreHorizontal className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
          {/* Right edge shadow for depth when scrolling */}
          <div className="absolute right-0 top-0 bottom-0 w-px bg-border-primary/30" />
        </div>

        {/* ── Scrollable columns section ── */}

        {/* Assignees - Inline Editable */}
        {(!visibleColumns || visibleColumns.has('assignees')) && (
          <div className="shrink-0" style={{ width: colW('assignees', 88) }} onClick={(e) => e.stopPropagation()}>
            <AssigneesEditor
              assignees={task.assignees || []}
              availableAssignees={availableAssignees}
              orgMemberNames={orgMemberNames}
              onAdd={handleAddAssignee}
              onRemove={handleRemoveAssignee}
              size="sm"
              maxDisplay={2}
              disabled={!canEdit}
            />
          </div>
        )}

        {/* Due Date - Inline Editable */}
        {(!visibleColumns || visibleColumns.has('due_date')) && (
          <div className="flex items-center shrink-0" style={{ width: colW('due_date', 110) }} onClick={(e) => e.stopPropagation()}>
            <InlineDatePicker
              value={task.dueDate}
              onChange={(dueDate) => handleUpdate({ dueDate })}
              size="sm"
              showTime={false}
              disabled={!canEdit}
              highlight={overdue && !isCompleted}
            />
          </div>
        )}

        {/* Start Date column - Inline Editable */}
        {visibleColumns?.has('start_date') && (
          <div className="shrink-0 flex items-center" style={{ width: colW('start_date', 110) }} onClick={(e) => e.stopPropagation()}>
            <InlineDatePicker
              value={task.startDate}
              onChange={(startDate) => handleUpdate({ startDate })}
              size="sm"
              showTime={false}
              disabled={!canEdit}
            />
          </div>
        )}

        {/* Time Estimated column - Inline Editable */}
        {visibleColumns?.has('time_estimated') && (
          <div className="shrink-0 flex items-center" style={{ width: colW('time_estimated', 80) }} onClick={(e) => e.stopPropagation()}>
            <InlineMinutesInput
              value={task.timeEstimated}
              onChange={(ms) => handleUpdate({ timeEstimated: ms })}
              disabled={!canEdit}
            />
          </div>
        )}

        {/* Tags column - Inline Editable */}
        {visibleColumns?.has('tags') && (
          <div className="shrink-0 flex items-center" style={{ width: colW('tags', 140) }} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <TagsEditor
              tags={task.tags || []}
              availableTags={availableTags}
              onAdd={(tag) => handleUpdate({ tags: [...(task.tags || []), tag] })}
              onRemove={(tag) => handleUpdate({ tags: (task.tags || []).filter(t => t !== tag) })}
              onCreateNew={handleCreateNewTag}
              size="sm"
              maxDisplay={2}
              disabled={!canEdit}
            />
          </div>
        )}

        {/* Created At column */}
        {visibleColumns?.has('created_at') && (
          <div className="shrink-0 flex items-center" style={{ width: colW('created_at', 110) }}>
            {task.dateCreated ? (
              <span className="text-[11px] text-text-tertiary">
                {new Date(task.dateCreated).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: '2-digit' })}
              </span>
            ) : (
              <span className="text-[11px] text-text-quaternary/50">—</span>
            )}
          </div>
        )}

        {/* Custom field columns - Inline Editable */}
        {customFieldCols.map(cf => {
          const colId = `cf_${cf.id}`;
          if (!visibleColumns?.has(colId)) return null;
          const cfValue = ((task.customFields?.[cf.id] ?? null) as CustomFieldValue);
          return (
            <div key={colId} className="shrink-0 flex items-center" style={{ width: colW(colId, 120) }} onClick={(e) => e.stopPropagation()}>
              <CustomFieldEditor
                field={cf}
                value={cfValue}
                onChange={(val) => handleUpdate({ customFields: { ...task.customFields, [cf.id]: val } })}
                disabled={!canEdit}
              />
            </div>
          );
        })}
      </div>
    </>
  );
}
